import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { PermissionProvider, usePermissions } from "@/lib/permissions";
import AppShell from "@/layout/AppShell";
import PermissionGate from "@/components/premium/PermissionGate";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Poultry from "@/pages/Poultry";
import Health from "@/pages/Health";
import Projects from "@/pages/Projects";
import Finance from "@/pages/Finance";
import Inventory from "@/pages/Inventory";
import Assets from "@/pages/Assets";
import Farm from "@/pages/Farm";
import Documents from "@/pages/Documents";
import Reports from "@/pages/Reports";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import Property from "@/pages/Property";
import Analytics from "@/pages/Analytics";
import Situation from "@/pages/Situation";
import GoLive from "@/pages/GoLive";
import PropertyMap from "@/pages/PropertyMap";
import HR from "@/pages/HR";
import Marketing from "@/pages/Marketing";
import CaregiverSales from "@/pages/CaregiverSales";
import PermissionManagement from "@/pages/PermissionManagement";
import UsersPage from "@/pages/Users";
import Audit from "@/pages/Audit";
import Intelligence from "@/pages/Intelligence";
import Knowledge from "@/pages/Knowledge";
import Briefs from "@/pages/Briefs";
import OperatingCentre from "@/pages/OperatingCentre";
import Feedback from "@/pages/Feedback";
import FirstLoginModal from "@/components/FirstLoginModal";

// Landing router: executives land on Operating Centre, everyone else on Dashboard.
function DefaultLanding() {
  const { isExecutive, has } = usePermissions();
  if (isExecutive && has("module:dashboard")) return <OperatingCentre />;
  return <Dashboard />;
}

function Protected() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-2">
          <div className="h-10 w-10 rounded-2xl bg-primary/20" />
          <div className="text-muted-foreground text-sm">Loading Home360…</div>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <PermissionProvider>
      <AppShell>
        <Outlet />
        <FirstLoginModal />
      </AppShell>
    </PermissionProvider>
  );
}

const Gate = ({ perm, children }) => <PermissionGate require={perm}>{children}</PermissionGate>;

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="bottom-right" richColors closeButton toastOptions={{ className: "rounded-2xl" }} />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<Protected />}>
              <Route path="/" element={<Gate perm="module:dashboard"><DefaultLanding /></Gate>} />
              <Route path="/dashboard" element={<Gate perm="module:dashboard"><Dashboard /></Gate>} />
              <Route path="/operating-centre" element={<Gate perm="module:dashboard"><OperatingCentre /></Gate>} />
              <Route path="/health"       element={<Gate perm="module:health"><Health /></Gate>} />
              <Route path="/sales"        element={<Gate perm="module:sales"><CaregiverSales /></Gate>} />
              <Route path="/poultry"      element={<Gate perm="module:poultry"><Poultry /></Gate>} />
              <Route path="/projects"     element={<Gate perm="module:projects"><Projects /></Gate>} />
              <Route path="/finance"      element={<Gate perm="module:finance"><Finance /></Gate>} />
              <Route path="/inventory"    element={<Gate perm="module:inventory"><Inventory /></Gate>} />
              <Route path="/assets"       element={<Gate perm="module:assets"><Assets /></Gate>} />
              <Route path="/farm"         element={<Gate perm="module:farm"><Farm /></Gate>} />
              <Route path="/hr"           element={<Gate perm="module:hr"><HR /></Gate>} />
              <Route path="/marketing"    element={<Gate perm="module:marketing"><Marketing /></Gate>} />
              <Route path="/documents"    element={<Gate perm="module:documents"><Documents /></Gate>} />
              <Route path="/reports"      element={<Gate perm="module:reports"><Reports /></Gate>} />
              <Route path="/notifications" element={<Gate perm="module:notifications"><Notifications /></Gate>} />
              <Route path="/analytics"    element={<Gate perm="module:analytics"><Analytics /></Gate>} />
              <Route path="/property"     element={<Gate perm="module:property"><Property /></Gate>} />
              <Route path="/map"          element={<Gate perm="module:map"><PropertyMap /></Gate>} />
              <Route path="/situation"    element={<Gate perm="module:situation"><Situation /></Gate>} />
              <Route path="/go-live"      element={<Gate perm="module:go_live"><GoLive /></Gate>} />
              <Route path="/settings"     element={<Gate perm="module:settings"><Settings /></Gate>} />
              <Route path="/users"        element={<Gate perm="admin:users:manage"><UsersPage /></Gate>} />
              <Route path="/permissions"  element={<Gate perm="admin:permissions:manage"><PermissionManagement /></Gate>} />
              <Route path="/audit"        element={<Gate perm="admin:audit:read"><Audit /></Gate>} />
              <Route path="/intelligence" element={<Gate perm="module:intelligence"><Intelligence /></Gate>} />
              <Route path="/knowledge"    element={<Gate perm="module:knowledge"><Knowledge /></Gate>} />
              <Route path="/briefs"       element={<Gate perm="module:briefs"><Briefs /></Gate>} />
              <Route path="/feedback"     element={<Gate perm="module:dashboard"><Feedback /></Gate>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
